import { capitalize } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

import { Attempt, AttemptType } from '../types';
import { getAttemptTitle } from '../utils';

export const AttemptModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [archiveAttempt, { isLoading: isArchivingAttempt }] =
    api.endpoints.archiveAttempt.useMutation();

  const [resendAttempt, { isLoading: isResendingAttempt }] =
    api.endpoints.resendAttempt.useMutation();

  const { activeAttempt, activeAttemptType: action } = useSelector(
    slices.webhooks.selectors.selectActiveAttempt,
  );

  if (action === 'view' || action === null) {
    return null;
  }

  const handleHide = () =>
    dispatch(slices.webhooks.actions.resetAttemptConfiguration());

  const handleActionConfirm = () => {
    const queries = {
      archive: archiveAttempt,
      resend: resendAttempt,
    };

    const query = queries[action];

    query({
      attempt: activeAttempt!,
    })
      ?.unwrap()
      .then(() => {
        toast({
          status: 'success',
          message: `${toastMessage?.success}`,
        });
      })
      .catch(() => {
        toast({
          status: 'danger',
          message: `${toastMessage?.failure}`,
        });
      })
      .finally(() => {
        handleHide();
      });
  };

  const isLoading = isArchivingAttempt || isResendingAttempt;

  const { children, isFullPage, variant, toastMessage } =
    resolveModalProps({ action, attempt: activeAttempt, intl }) ?? {};

  const modalProps = {
    children,
    isFullPage,
    isVisible: Boolean(action),
    primaryAction: {
      isLoading,
      onClick: handleActionConfirm,
      text: `${capitalize(action ?? '')}`,
      variant,
    },
    onCancel: handleHide,
    onHide: handleHide,
    title: getAttemptTitle(action, intl),
  };

  return <Modal {...modalProps} />;
};

const resolveModalProps = ({
  action,
  attempt,
  intl,
}: {
  action: Nullable<AttemptType>;
  attempt: Nullable<Attempt>;
  intl: any;
}) => {
  switch (action) {
    case 'archive':
      return {
        children: (
          <Text>
            <FormattedMessage
              id="AttemptModal.archiveConfirmation"
              defaultMessage="Are you sure you want to archive {endpointUrl}?"
              description="Confirmation message for archiving attempt. Example: endpointUrl =https://api.example.com/webhook"
              values={{ endpointUrl: attempt?.endpointUrl ?? '' }}
            />
          </Text>
        ),
        variant: 'danger' as const,
        isFullPage: false,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'AttemptModal.archiveSuccess',
              defaultMessage: 'Attempt {endpointUrl} (<{attemptId}>) archived.',
              description:
                'Success message for attempt archival. Example: endpointUrl = https://api.example.com/webhook, attemptId = "abc123"',
            },
            { endpointUrl: attempt?.endpointUrl, attemptId: attempt?.id },
          ),
          failure: intl.formatMessage({
            id: 'AttemptModal.archiveFailure',
            defaultMessage: 'Failed to archived attempt.',
            description: 'Error message for attempt archival',
          }),
        },
      };
    case 'resend':
      return {
        children: (
          <Text>
            <FormattedMessage
              id="AttemptModal.resendConfirmation"
              defaultMessage="Are you sure you want to resend {endpointUrl}"
              description="Confirmation message for resending attempt. Example: endpointUrl =https://api.example.com/webhook"
              values={{
                endpointUrl: attempt?.endpointUrl ?? '',
              }}
            />
          </Text>
        ),
        variant: 'primary' as const,
        isFullPage: false,
        toastMessage: {
          success: intl.formatMessage(
            {
              id: 'AttemptModal.resendSuccess',
              defaultMessage: 'Attempt {endpointUrl} (<{attemptId}>) resent.',
              description:
                'Success message for attempt resend. Example: endpointUrl = https://api.example.com/webhook, attemptId = "abc123"',
            },
            { endpointUrl: attempt?.endpointUrl, attemptId: attempt?.id },
          ),
          failure: intl.formatMessage({
            id: 'AttemptModal.resendFailure',
            defaultMessage: 'Failed to resend attempt.',
            description: 'Error message for attempt resend',
          }),
        },
      };
    case 'view':
    default:
      return null;
  }
};
